
import {
  Component, Mixins, Watch, Vue,
} from 'vue-property-decorator';
import Navbar from '@/features/common/components/navbar/index.vue';
import Notification from '@/features/common/components/Notification.vue';
import InactiveUserPopover from '@/features/common/components/InactiveUserPopover.vue';
import Loading from '@/features/common/components/Loading.vue';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { AlertDialogType } from '@/features/common/types';
import {
  truncateText, getProfileInitials, scrollToInPage,
} from '@/features/common/helpers';
import { setLiveChat } from '@/features/common/plugins/liveChat';
import WhiteLabelMixin from './features/common/mixins/whiteLabelMixin';
import { UserInfo } from './features/users/types';
import { exceptionsAndPagesToRedirect } from './features/common/constants';
import AppTopWarning from './features/common/components/AppTopWarning.vue';
import { getFeatureValue } from './features/common/helpers/feature-flag';
import { setGA4Environment } from './features/common/helpers/google-analytics';

@Component({
  components: {
    Navbar,
    Notification,
    InactiveUserPopover,
    Loading,
    AppTopWarning,
  },
})
export default class App extends Mixins(WhiteLabelMixin) {
  private isInactivePopover = false;

  private truncateText = truncateText;

  private getProfileInitials = getProfileInitials;

  private appTopWarningMessage = '';

  get notification(): boolean {
    return this.$store.state.notification;
  }

  set notification(value: boolean) {
    this.$store.commit('setNotification', value);
  }

  get loading(): boolean {
    return this.$store.state.loading || this.$store.state.isCheckoutLoading;
  }

  get isAuth(): boolean {
    return this.$store.getters.getIsAuth;
  }

  get showNavbar(): boolean {
    return this.isAuth && !this.isPageWithoutNav;
  }

  get confirmationDialog(): boolean {
    return this.$store.state.confirmDialog;
  }

  get alertDialogs(): AlertDialogType[] {
    return this.$store.state.alertDialogs;
  }

  get userInfo(): UserInfo {
    return this.$store.getters.getUserInfo;
  }

  get routePath(): string {
    return this.$route.path as string;
  }

  @Watch('userInfo')
  private showLiveChat() {
    if (!this.userInfo || !this.userInfo.chatId) {
      return;
    }

    setLiveChat(this.userInfo);
  }

  @Watch('userInfo')
  private async setGrowthBookAttributes() {
    if (!this.userInfo || !this.userInfo.account) {
      return;
    }

    try {
      const growthBook = await Vue.prototype.initGrowthBook();
      growthBook.setAttributes({
        id: this.userInfo.account,
        user_id: String(this.userInfo.userId),
        account: this.userInfo.account,
        is_new: !this.userInfo.isLC,
      });
    } catch (err) {
      // console.log('err: ', err);
    }
  }

  async mounted() {
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('storage', this.handleStorageEvent);

    this.appTopWarningMessage = await getFeatureValue('webshipv2-top-page-warning') || '';
    setGA4Environment(this.$gtag);
  }

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('storage', this.handleStorageEvent);
  }

  private async handleStorageEvent(event: StorageEvent) {
    if (event.key === 'auth_event') {
      const data = JSON.parse(event.newValue || '{}');

      if (data.type === 'login' || data.type === 'logout') {
        await this.$store.dispatch('init');
      }
    }
  }

  private handleScroll = () => {
    const scrollToTopBtn = document.getElementById('scrollToTopBtn');
    const historyControls = document.querySelector('.history-controls');

    if (!scrollToTopBtn) {
      return;
    }

    if (historyControls) {
      scrollToTopBtn.style.display = 'none';
      scrollToTopBtn.style.opacity = '0';
      return;
    }

    if (window.scrollY > 400) {
      scrollToTopBtn.style.display = 'block';
      scrollToTopBtn.style.opacity = '1';
      return;
    }

    scrollToTopBtn.style.display = 'none';
    scrollToTopBtn.style.opacity = '0';
  }

  private scrollToTop = () => {
    scrollToInPage(0, 0, true);
  }

  @Watch('userInfo')
  @Watch('routePath')
  private async onLoadUserInfo(userInfo: UserInfo) {
    await this.$nextTick();
    if (!userInfo) return;

    const exceptionPages = exceptionsAndPagesToRedirect.map((item) => item.page);
    const isExceptionPages = exceptionPages.includes(this.$route.path as string);

    this.isInactivePopover = !!userInfo.isShowCheckIn && !isExceptionPages;
  }

  @Watch('notification')
  private closeNotificationEvent = (newValue: boolean) => {
    if (newValue) return;
    eventBus.$off(eventNames.NOTIFICATION_SUBMIT);
  }

  created(): void {
    this.$store.dispatch('init');
  }

  private hideDialog(id: number): void {
    this.$store.commit('hideAlertDialog', id);
  }

  private redirectTo = (location: string, target = '_self') => {
    window.open(location, target);
  }
}
